import React, { useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  FaChartPie,
  FaCheckCircle,
  FaCopy,
  FaDownload,
  FaEraser,
  FaExclamationTriangle,
  FaHashtag,
  FaPlus,
  FaTrash,
} from "react-icons/fa";

const COLORS = {
  bg: "#F6F4F0",
  ink: "#17161B",
  panel: "#FFFFFF",
  dark: "#111014",
  accent: "#E8481D",
  accentSoft: "#FCE7DF",
  green: "#16845B",
  greenSoft: "#E7F6EF",
  yellow: "#A76500",
  yellowSoft: "#FFF4D6",
  muted: "#77737F",
  border: "#E7E3DC",
};

// Context limits are editable because provider limits can change.
const MODEL_PRESETS = {
  custom: { name: "Custom context window", context: 128000 },
  "gpt-modern": { name: "Modern GPT model — 128K", context: 128000 },
  "gpt-long": { name: "Long-context GPT model — 400K", context: 400000 },
  "claude-modern": { name: "Modern Claude model — 200K", context: 200000 },
  "gemini-long": { name: "Long-context Gemini model — 1M", context: 1000000 },
};

const SAMPLE_PROMPT = `You are a helpful SEO assistant.

Create a clear content brief for the keyword “AI visibility checker”.

Requirements:
- Identify the search intent
- Suggest an H1 and five H2 sections
- Include FAQs
- Keep the answer under 800 words`;

const emptyMessage = (role = "user") => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  role,
  content: "",
});

const countMatches = (text, regex) => (text.match(regex) || []).length;

// This is a transparent browser-only estimate, not a provider tokenizer.
// Latin prose averages near 4 chars/token; Bengali/CJK/emoji usually consume more.
const estimateText = (value) => {
  const text = String(value || "");
  if (!text) {
    return {
      tokens: 0,
      low: 0,
      high: 0,
      characters: 0,
      charactersNoSpaces: 0,
      words: 0,
      lines: 0,
      bytes: 0,
      bengaliChars: 0,
      nonLatinChars: 0,
    };
  }

  const characters = Array.from(text).length;
  const charactersNoSpaces = Array.from(text.replace(/\s/g, "")).length;
  const words = text.trim() ? text.trim().split(/\s+/u).length : 0;
  const lines = text.split(/\r?\n/).length;
  const bytes = new TextEncoder().encode(text).length;
  const bengaliChars = countMatches(text, /[\u0980-\u09FF]/gu);
  const cjkChars = countMatches(
    text,
    /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af]/gu
  );
  const emojiChars = countMatches(
    text,
    /\p{Extended_Pictographic}/gu
  );
  const nonLatinChars = countMatches(text, /[^\u0000-\u024F\s]/gu);
  const codeSymbols = countMatches(text, /[{}\[\]<>_=+*/\\|`~^]/g);
  const urls = countMatches(text, /https?:\/\/\S+/gi);

  const latinLike = Math.max(
    0,
    charactersNoSpaces - bengaliChars - cjkChars - emojiChars
  );
  const raw =
    latinLike / 3.8 +
    bengaliChars / 1.45 +
    cjkChars / 1.05 +
    emojiChars * 2.2 +
    codeSymbols * 0.45 +
    urls * 3 +
    Math.max(0, words - 1) * 0.12;
  const tokens = Math.max(1, Math.ceil(raw));

  return {
    tokens,
    low: Math.max(1, Math.floor(tokens * 0.85)),
    high: Math.max(1, Math.ceil(tokens * 1.18)),
    characters,
    charactersNoSpaces,
    words,
    lines,
    bytes,
    bengaliChars,
    nonLatinChars,
  };
};

const PromptTokenCounter = () => {
  const [mode, setMode] = useState("single");
  const [prompt, setPrompt] = useState("");
  const [messages, setMessages] = useState([
    emptyMessage("system"),
    emptyMessage("user"),
  ]);
  const [modelKey, setModelKey] = useState("gpt-modern");
  const [contextLimit, setContextLimit] = useState(
    String(MODEL_PRESETS["gpt-modern"].context)
  );
  const [reservedOutput, setReservedOutput] = useState("2000");

  const applyModel = (key) => {
    setModelKey(key);
    setContextLimit(String(MODEL_PRESETS[key].context));
    console.log("[PROMPT TOKEN COUNTER] Context preset applied.", {
      key,
      ...MODEL_PRESETS[key],
    });
  };

  const analysis = useMemo(() => {
    if (mode === "single") {
      const stats = estimateText(prompt);
      return { ...stats, messageOverhead: 0, contentTokens: stats.tokens };
    }

    const items = messages.map((message) => ({
      ...message,
      stats: estimateText(message.content),
    }));
    const totals = items.reduce(
      (sum, item) => ({
        tokens: sum.tokens + item.stats.tokens,
        low: sum.low + item.stats.low,
        high: sum.high + item.stats.high,
        characters: sum.characters + item.stats.characters,
        charactersNoSpaces:
          sum.charactersNoSpaces + item.stats.charactersNoSpaces,
        words: sum.words + item.stats.words,
        lines: sum.lines + item.stats.lines,
        bytes: sum.bytes + item.stats.bytes,
        bengaliChars: sum.bengaliChars + item.stats.bengaliChars,
        nonLatinChars: sum.nonLatinChars + item.stats.nonLatinChars,
      }),
      {
        tokens: 0,
        low: 0,
        high: 0,
        characters: 0,
        charactersNoSpaces: 0,
        words: 0,
        lines: 0,
        bytes: 0,
        bengaliChars: 0,
        nonLatinChars: 0,
      }
    );
    const nonEmptyMessages = items.filter((item) => item.content.trim()).length;
    // Common chat serialization overhead estimate: ~4 tokens/message + 3 reply priming.
    const messageOverhead = nonEmptyMessages ? nonEmptyMessages * 4 + 3 : 0;

    return {
      ...totals,
      contentTokens: totals.tokens,
      tokens: totals.tokens + messageOverhead,
      low: totals.low + messageOverhead,
      high: totals.high + messageOverhead,
      messageOverhead,
      items,
    };
  }, [mode, prompt, messages]);

  const limit = Math.max(1, Number(contextLimit) || 1);
  const output = Math.max(0, Number(reservedOutput) || 0);
  const totalWithOutput = analysis.tokens + output;
  const remaining = Math.max(0, limit - totalWithOutput);
  const usage = Math.min(100, (totalWithOutput / limit) * 100);
  const overLimit = totalWithOutput > limit;
  const warning = !overLimit && usage >= 80;

  const currentText =
    mode === "single"
      ? prompt
      : messages
          .map((item) => `${item.role.toUpperCase()}:\n${item.content}`)
          .join("\n\n");

  const updateMessage = (id, field, value) => {
    setMessages((current) =>
      current.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const removeMessage = (id) => {
    setMessages((current) => current.filter((item) => item.id !== id));
  };

  const clearAll = () => {
    setPrompt("");
    setMessages([emptyMessage("system"), emptyMessage("user")]);
    console.log("[PROMPT TOKEN COUNTER] Content cleared.");
    toast.success("Content cleared.");
  };

  const copyReport = async () => {
    const report = [
      "Prompt Token Counter",
      `Estimated tokens: ${analysis.tokens.toLocaleString()}`,
      `Estimated range: ${analysis.low.toLocaleString()}–${analysis.high.toLocaleString()}`,
      `Characters: ${analysis.characters.toLocaleString()}`,
      `Words: ${analysis.words.toLocaleString()}`,
      `Context usage: ${usage.toFixed(2)}%`,
      `Remaining after reserved output: ${remaining.toLocaleString()}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(report);
      toast.success("Token report copied.");
    } catch (error) {
      console.error("[PROMPT TOKEN COUNTER] Copy failed.", error);
      toast.error("Could not copy the report.");
    }
  };

  const downloadCsv = () => {
    const rows = [
      ["Metric", "Value"],
      ["Estimated tokens", analysis.tokens],
      ["Low estimate", analysis.low],
      ["High estimate", analysis.high],
      ["Characters", analysis.characters],
      ["Characters without spaces", analysis.charactersNoSpaces],
      ["Words", analysis.words],
      ["Lines", analysis.lines],
      ["UTF-8 bytes", analysis.bytes],
      ["Message overhead", analysis.messageOverhead],
      ["Reserved output tokens", output],
      ["Context limit", limit],
      ["Remaining tokens", remaining],
      ["Context usage percent", usage.toFixed(2)],
    ];
    const csv = rows.map((row) => row.join(",")).join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" })
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "prompt-token-report.csv";
    anchor.click();
    URL.revokeObjectURL(url);
    console.log("[PROMPT TOKEN COUNTER] CSV downloaded.");
    toast.success("CSV downloaded.");
  };

  const statCards = [
    ["Estimated tokens", analysis.tokens.toLocaleString(), "Primary result"],
    ["Characters", analysis.characters.toLocaleString(), "Including spaces"],
    ["Words", analysis.words.toLocaleString(), "Whitespace-separated"],
    ["UTF-8 bytes", analysis.bytes.toLocaleString(), "Payload size"],
  ];

  return (
    <div style={{ background: COLORS.bg, color: COLORS.ink, minHeight: "100vh" }}>
      <ToastContainer position="top-right" autoClose={2600} />
      <div className="container py-5" style={{ maxWidth: 1180 }}>
      

        <div
          className="card border-0 shadow-sm mb-4"
          style={{ borderRadius: 18, background: COLORS.panel }}
        >
          <div className="card-body p-3 p-md-4">
            <div
              className="d-inline-flex p-1 rounded-3 mb-4"
              style={{ background: COLORS.bg }}
            >
              {["single", "chat"].map((item) => (
                <button
                  key={item}
                  type="button"
                  className="btn border-0 px-3"
                  onClick={() => setMode(item)}
                  style={{
                    background: mode === item ? COLORS.panel : "transparent",
                    boxShadow: mode === item ? "0 2px 8px rgba(0,0,0,.08)" : "none",
                    fontWeight: 700,
                  }}
                >
                  {item === "single" ? "Single prompt" : "Chat messages"}
                </button>
              ))}
            </div>

            {mode === "single" ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label htmlFor="promptText" className="form-label fw-bold mb-0">
                    Prompt text
                  </label>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setPrompt(SAMPLE_PROMPT)}
                    style={{ color: COLORS.accent, fontWeight: 700 }}
                  >
                    Use sample
                  </button>
                </div>
                <textarea
                  id="promptText"
                  className="form-control"
                  rows="11"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder="Paste or type your prompt here..."
                  style={{ borderColor: COLORS.border, borderRadius: 12, resize: "vertical" }}
                />
              </div>
            ) : (
              <div>
                {messages.map((message, index) => (
                  <div
                    key={message.id}
                    className="border rounded-3 p-3 mb-3"
                    style={{ borderColor: `${COLORS.border} !important` }}
                  >
                    <div className="d-flex gap-2 align-items-center mb-2">
                      <select
                        className="form-select form-select-sm"
                        aria-label={`Message ${index + 1} role`}
                        value={message.role}
                        onChange={(event) =>
                          updateMessage(message.id, "role", event.target.value)
                        }
                        style={{ maxWidth: 140 }}
                      >
                        <option value="system">System</option>
                        <option value="user">User</option>
                        <option value="assistant">Assistant</option>
                      </select>
                      <span className="small" style={{ color: COLORS.muted }}>
                        Message {index + 1}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm ms-auto"
                        aria-label={`Remove message ${index + 1}`}
                        disabled={messages.length === 1}
                        onClick={() => removeMessage(message.id)}
                        style={{ color: COLORS.accent }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                    <textarea
                      className="form-control"
                      rows="4"
                      value={message.content}
                      onChange={(event) =>
                        updateMessage(message.id, "content", event.target.value)
                      }
                      placeholder={`${message.role} message...`}
                      style={{ borderColor: COLORS.border, borderRadius: 10 }}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() =>
                    setMessages((current) => [...current, emptyMessage("user")])
                  }
                >
                  <FaPlus className="me-2" /> Add message
                </button>
              </div>
            )}

            <div className="row g-3 mt-2">
              <div className="col-md-6">
                <label htmlFor="modelPreset" className="form-label fw-bold">
                  Context preset
                </label>
                <select
                  id="modelPreset"
                  className="form-select"
                  value={modelKey}
                  onChange={(event) => applyModel(event.target.value)}
                >
                  {Object.entries(MODEL_PRESETS).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-sm-6 col-md-3">
                <label htmlFor="contextLimit" className="form-label fw-bold">
                  Context limit
                </label>
                <input
                  id="contextLimit"
                  type="number"
                  min="1"
                  className="form-control"
                  value={contextLimit}
                  onChange={(event) => {
                    setContextLimit(event.target.value);
                    setModelKey("custom");
                  }}
                />
              </div>
              <div className="col-sm-6 col-md-3">
                <label htmlFor="reservedOutput" className="form-label fw-bold">
                  Reserved output
                </label>
                <input
                  id="reservedOutput"
                  type="number"
                  min="0"
                  className="form-control"
                  value={reservedOutput}
                  onChange={(event) => setReservedOutput(event.target.value)}
                />
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                className="btn text-white px-3"
                onClick={copyReport}
                disabled={!currentText.trim()}
                style={{ background: COLORS.accent, fontWeight: 700 }}
              >
                <FaCopy className="me-2" /> Copy report
              </button>
              <button
                type="button"
                className="btn btn-outline-dark"
                onClick={downloadCsv}
                disabled={!currentText.trim()}
              >
                <FaDownload className="me-2" /> Download CSV
              </button>
              <button type="button" className="btn btn-light" onClick={clearAll}>
                <FaEraser className="me-2" /> Clear
              </button>
            </div>
          </div>
        </div>

        <div className="row g-3 mb-4">
          {statCards.map(([label, value, help]) => (
            <div className="col-sm-6 col-lg-3" key={label}>
              <div className="card border-0 h-100 shadow-sm" style={{ borderRadius: 15 }}>
                <div className="card-body">
                  <div className="small text-uppercase fw-bold" style={{ color: COLORS.muted }}>
                    {label}
                  </div>
                  <div className="fs-3 fw-bold my-1">{value}</div>
                  <div className="small" style={{ color: COLORS.muted }}>{help}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="row g-4">
          <div className="col-lg-7">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 18 }}>
              <div className="card-body p-4">
                <h2 className="h5 fw-bold mb-4">
                  <FaChartPie className="me-2" style={{ color: COLORS.accent }} />
                  Context-window usage
                </h2>
                <div className="d-flex justify-content-between small fw-bold mb-2">
                  <span>{totalWithOutput.toLocaleString()} planned tokens</span>
                  <span>{usage.toFixed(2)}%</span>
                </div>
                <div className="progress mb-3" style={{ height: 12, background: COLORS.bg }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    aria-valuenow={usage}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    style={{
                      width: `${usage}%`,
                      background: overLimit
                        ? COLORS.accent
                        : warning
                        ? COLORS.yellow
                        : COLORS.green,
                    }}
                  />
                </div>

                <div
                  className="rounded-3 p-3 mb-3"
                  style={{
                    background: overLimit
                      ? COLORS.accentSoft
                      : warning
                      ? COLORS.yellowSoft
                      : COLORS.greenSoft,
                  }}
                >
                  {overLimit ? (
                    <div style={{ color: COLORS.accent }}>
                      <FaExclamationTriangle className="me-2" />
                      Over the selected context limit by{" "}
                      <strong>{(totalWithOutput - limit).toLocaleString()} tokens</strong>.
                    </div>
                  ) : (
                    <div style={{ color: warning ? COLORS.yellow : COLORS.green }}>
                      <FaCheckCircle className="me-2" />
                      Approximately <strong>{remaining.toLocaleString()} tokens</strong>{" "}
                      remain after the reserved output.
                    </div>
                  )}
                </div>

                <div className="row g-2 small">
                  <div className="col-6">Prompt estimate</div>
                  <div className="col-6 text-end fw-bold">{analysis.tokens.toLocaleString()}</div>
                  <div className="col-6">Likely range</div>
                  <div className="col-6 text-end fw-bold">
                    {analysis.low.toLocaleString()}–{analysis.high.toLocaleString()}
                  </div>
                  <div className="col-6">Reserved output</div>
                  <div className="col-6 text-end fw-bold">{output.toLocaleString()}</div>
                  <div className="col-6">Selected context</div>
                  <div className="col-6 text-end fw-bold">{limit.toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div
              className="card border-0 h-100 text-white"
              style={{ borderRadius: 18, background: COLORS.dark }}
            >
              <div className="card-body p-4">
                <h2 className="h5 fw-bold mb-4">
                  <FaHashtag className="me-2" style={{ color: COLORS.accent }} />
                  Detailed breakdown
                </h2>
                <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                  <span>Characters without spaces</span>
                  <strong>{analysis.charactersNoSpaces.toLocaleString()}</strong>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                  <span>Lines</span>
                  <strong>{analysis.lines.toLocaleString()}</strong>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                  <span>Bengali characters</span>
                  <strong>{analysis.bengaliChars.toLocaleString()}</strong>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                  <span>Non-Latin characters</span>
                  <strong>{analysis.nonLatinChars.toLocaleString()}</strong>
                </div>
                {mode === "chat" && (
                  <div className="d-flex justify-content-between py-2 border-bottom border-secondary">
                    <span>Chat overhead estimate</span>
                    <strong>{analysis.messageOverhead.toLocaleString()}</strong>
                  </div>
                )}
                <p className="small mt-4 mb-0" style={{ color: "#C8C4CE" }}>
                  Privacy: your prompt stays in this browser. Nothing is uploaded by
                  this component.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div
          className="rounded-3 p-3 mt-4 small"
          style={{ background: COLORS.yellowSoft, color: "#694400" }}
        >
          <FaExclamationTriangle className="me-2" />
          <strong>Accuracy note:</strong> this dependency-free browser version provides
          a language-aware estimate. Exact counts require the exact tokenizer and
          message serialization used by the selected API model.
        </div>
      </div>
    </div>
  );
};

export default PromptTokenCounter;

